import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import CompletionStatusModal from './CompletionStatusModal';

const ProgressionGrid = ({ characters, contentAreas, isOfficer, onStatusUpdate }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    character: null,
    content: null,
    currentStatus: null
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { name: 'CheckCircle2', color: 'var(--color-success)' };
      case 'in-progress':
        return { name: 'Clock', color: 'var(--color-warning)' };
      default:
        return { name: 'Circle', color: 'var(--color-muted-foreground)' };
    }
  };

  const handleCellClick = (character, content, status) => {
    if (isOfficer) {
      setModalState({
        isOpen: true,
        character,
        content,
        currentStatus: status
      });
    }
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      character: null,
      content: null,
      currentStatus: null
    });
  };

  const handleStatusUpdate = (characterId, contentId, newStatus, notes) => {
    onStatusUpdate(characterId, contentId, newStatus, notes);
  };

  return (
    <>
      <div className="bg-card rounded-xl border border-border shadow-warm overflow-hidden">
        {/* Desktop Grid View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground sticky left-0 bg-muted/50 z-10">
                  Character
                </th>
                {contentAreas?.map((content) => (
                  <th 
                    key={content?.id}
                    className="px-4 py-4 text-center text-xs font-medium text-foreground min-w-[120px]"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <Icon name={content?.icon} size={16} />
                      <span>{content?.name}</span>
                    </div>
                  </th>
                ))}
                <th className="px-6 py-4 text-center text-sm font-semibold text-foreground">
                  Progress
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {characters?.map((character) => (
                <tr key={character?.id} className="hover:bg-muted/30 transition-base">
                  <td className="px-6 py-4 sticky left-0 bg-card z-10">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center text-accent-foreground font-semibold"
                      >
                        {character?.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{character?.name}</p>
                        <p className="text-xs text-muted-foreground">{character?.class} {character?.level}</p>
                      </div>
                    </div>
                  </td>
                  {contentAreas?.map((content) => {
                    const status = character?.contentProgress?.[content?.id] || 'not-started';
                    const statusIcon = getStatusIcon(status);
                    return (
                      <td 
                        key={content?.id}
                        className={`px-4 py-4 text-center ${isOfficer ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                        onClick={() => handleCellClick(character, content, status)}
                      >
                        <div className="flex justify-center">
                          <Icon 
                            name={statusIcon?.name} 
                            size={20} 
                            color={statusIcon?.color}
                          />
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-semibold text-foreground">
                        {character?.completionPercentage}%
                      </span>
                      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-success transition-all duration-250"
                          style={{ width: `${character?.completionPercentage}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-border">
          {characters?.map((character) => (
            <div key={character?.id} className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center text-accent-foreground font-semibold"
                  >
                    {character?.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{character?.name}</p>
                    <p className="text-xs text-muted-foreground">{character?.class} {character?.level}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-foreground">{character?.completionPercentage}%</p>
                  <p className="text-xs text-muted-foreground">Complete</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {contentAreas?.map((content) => {
                  const status = character?.contentProgress?.[content?.id] || 'not-started';
                  const statusIcon = getStatusIcon(status);
                  return (
                    <div 
                      key={content?.id}
                      className={`flex items-center gap-2 p-3 rounded-lg bg-muted/30 ${isOfficer ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                      onClick={() => handleCellClick(character, content, status)}
                    >
                      <Icon name={statusIcon?.name} size={16} color={statusIcon?.color} />
                      <span className="text-xs text-foreground">{content?.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <CompletionStatusModal
        isOpen={modalState?.isOpen}
        onClose={handleModalClose}
        character={modalState?.character}
        content={modalState?.content}
        currentStatus={modalState?.currentStatus}
        onUpdate={handleStatusUpdate}
      />
    </>
  );
};

export default ProgressionGrid;